import React from "react"
import {Helmet} from "react-helmet"
import {useStaticQuery, graphql} from "gatsby"

type SEOProps = {
    description?: string
    lang?: string
    meta?: Meta[]
    title: string
    structuredData?: boolean
}

type Meta =
    | { name: string; content: string; property?: never }
    | { property: string; content: string; name?: never }

type QueryTypes = {
    site: {
        siteMetadata: {
            title: string
            description: string
            author: string
            keywords: string[]
            siteUrl: string
            organization?: {
                name: string
                url: string
                logo: string
            }
            social?: {
                instagram?: string
            }
        }
    }
}

function SEO({
                 description = "",
                 lang = "pt-br",
                 meta = [],
                 title,
                 structuredData = true
             }: SEOProps) {

    const {site} = useStaticQuery<QueryTypes>(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          keywords
          siteUrl
          organization {
            name
            url
            logo
          }
          social {
            instagram
          }
        }
      }
    }
  `)

    const metaDescription = description || site.siteMetadata.description
    const fullTitle = `${title} | ${site.siteMetadata.title}`
    const canonicalUrl = typeof window !== "undefined" ? window.location.href : site.siteMetadata.siteUrl

    const jsonLd = site.siteMetadata.organization && structuredData
        ? {
            "@context": "https://schema.org",
            "@type": "SportsActivityLocation",
            "name": site.siteMetadata.organization.name,
            "url": site.siteMetadata.organization.url,
            "description": metaDescription,
            "image": `${site.siteMetadata.siteUrl}${site.siteMetadata.organization.logo}`,
            "address": {
                "@type": "PostalAddress",
                "addressLocality": "Campinas",
                "addressRegion": "SP",
                "addressCountry": "BR"
            },
            "founder": {
                "@type": "Person",
                "name": site.siteMetadata.author,
                "sameAs": site.siteMetadata.social?.instagram || ""
            }
        }
        : null

    return (
        <Helmet
            htmlAttributes={{lang}}
            title={title}
            titleTemplate={`%s | ${site.siteMetadata.title}`}
            link={[
                {
                    rel: "canonical",
                    href: canonicalUrl
                }
            ]}
            meta={[
                {
                    name: "description",
                    content: metaDescription
                },
                {
                    name: "keywords",
                    content: site.siteMetadata.keywords.join(", ")
                },
                {
                    property: "og:title",
                    content: fullTitle
                },
                {
                    property: "og:description",
                    content: metaDescription
                },
                {
                    property: "og:type",
                    content: "website"
                },
                {
                    name: "twitter:card",
                    content: "summary"
                },
                {
                    name: "twitter:creator",
                    content: site.siteMetadata.author
                },
                {
                    name: "twitter:title",
                    content: fullTitle
                },
                {
                    name: "twitter:description",
                    content: metaDescription
                },
                {
                    name: "robots",
                    content: "index,follow"
                }
            ].concat(meta)}
        >
            {jsonLd && (
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            )}
        </Helmet>
    )
}

export default SEO
